import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { updateLastActivity } from '../../Utils/LastActivity';
import type { UserContextProps } from '../../Contexts/UserContext';
import UserContext from '../../Contexts/UserContext';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import type { JSX } from 'react';
import { ErrorMessage } from 'shouldve-been-mui';
import type Folder from '../../Models/FolderModel';
import type { IndexedDBContextProps } from '../../Contexts/IndexedDBContext';
import IndexedDBContext from '../../Contexts/IndexedDBContext';
import { shakeForm } from 'shouldve-been-react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import type { SelectChangeEvent } from '@mui/material/Select';
import Select from '@mui/material/Select';
import CommentItem from '../../Models/CommentItemModel';
import ImageItem from '../../Models/ImageItemModel';
import MenuItem from '@mui/material/MenuItem';
import type { Theme} from '@mui/material';
import { useTheme } from '@mui/material';

type MoveItemsDialogProps = {
    controller: boolean,
    onClose: () => void,
    openedFolder: Folder | null,
    folders: Folder[],
    selectedRows: string[],
    commentItems: CommentItem[],
    imageItems: ImageItem[],
};

function MoveItemsDialog(props: Readonly<MoveItemsDialogProps>): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);

    const currentTheme: Theme = useTheme();

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);
    
    const [targetFolderId, setTargetFolderId] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);
    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());

    const commentItemsToMove: CommentItem[] = useMemo<CommentItem[]>(() => props.commentItems.filter((commentItem: CommentItem) => props.selectedRows.includes(commentItem.commentItemId)), [props.commentItems, props.selectedRows]);
    const imageItemsToMove: ImageItem[] = useMemo<ImageItem[]>(() => props.imageItems.filter((imageItem: ImageItem) => props.selectedRows.includes(imageItem.imageItemId)), [props.imageItems, props.selectedRows]);

    const selectMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        const sortedFolders: Folder[] = [...props.folders].sort((a: Folder, b: Folder) => a.changedDate.getTime() - b.changedDate.getTime());

        return (
            [
                <MenuItem 
                    key='__empty__'
                    value=''
                >
                    &nbsp;
                </MenuItem>,
                <MenuItem 
                    key='root'
                    value='root'
                >
                    &lt;root&gt;
                </MenuItem>,
            ].concat(sortedFolders.map((currentFolder: Folder) => {
                return (
                    <MenuItem
                        key={currentFolder.folderId}
                        value={currentFolder.folderId}
                    >
                        {currentFolder.name}
                    </MenuItem>
                );
            }))
        );
    }, [props.folders]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        setTargetFolderId('');
    }, [rerenderFlag]);

    async function handleSave(): Promise<void> {

        if (targetFolderId === '') {
            setErrorMessage('No destination selected');
            shakeForm(formRef);
            return;
        }

        if ((props.openedFolder === null && targetFolderId === 'root') || (targetFolderId === props.openedFolder?.folderId)) {
            setErrorMessage('Destination cannot be the same as the origin');
            shakeForm(formRef);
            return;
        }

        const newChangedDate: Date = new Date();

        for (let i: number = 0; i < commentItemsToMove.length; i++) {

            const currentCommentItem: CommentItem | undefined = commentItemsToMove[i];

            let success: boolean = false;

            if (currentCommentItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await indexedDBContext.indexedDBService.updateCommentItem(new CommentItem(currentCommentItem.commentItemId, currentCommentItem.name, currentCommentItem.createdDate, newChangedDate, currentCommentItem.comment, targetFolderId === 'root' ? null : targetFolderId));
            }
        
            if (!success) {
                setErrorMessage('Comment item with name ' + (currentCommentItem?.name ?? 'ERROR') + ' failed to update');
                shakeForm(formRef);
                return;
            }
        }

        for (let i: number = 0; i < imageItemsToMove.length; i++) {

            const currentImageItem: ImageItem | undefined = imageItemsToMove[i];

            let success: boolean = false;

            if (currentImageItem !== undefined) {
                // eslint-disable-next-line no-await-in-loop
                success = await indexedDBContext.indexedDBService.updateImageItem(new ImageItem(currentImageItem.imageItemId, currentImageItem.name, currentImageItem.createdDate, newChangedDate, currentImageItem.imageBlob, currentImageItem.fileName, targetFolderId === 'root' ? null : targetFolderId));
            }
        
            if (!success) {
                setErrorMessage('Image item with name ' + (currentImageItem?.name ?? 'ERROR') + ' failed to update');
                shakeForm(formRef);
                return;
            }
        }

        handleClose();
    }

    function handleClose(): void {

        updateLastActivity(userContext, localStorageContext);

        setTargetFolderId('');

        props.onClose();
    }

    return (
        <Dialog 
            open={props.controller} 
            onClose={handleClose}
            TransitionProps={{
                onEntered: (): void => setRerenderFlag(new Date()),
            }}
            PaperProps={{
                sx: {
                    position: 'fixed',
                },
            }}
        >
            <DialogTitle>Move Items</DialogTitle>
            <DialogContent>
                <p style={{
                    color: currentTheme.palette.text.primary,
                }}>
                    All selected comment and image items will be moved. Folders, checklists and observations cannot be moved.
                </p>
                <form noValidate autoComplete='off' ref={formRef} name='moveItems'>
                    <FormControl 
                        size='small'
                        style={{
                            width: Math.min(windowSize[0] * 0.8, 500),
                        }}
                    >
                        <InputLabel>Destination</InputLabel>
                        <Select
                            value={targetFolderId}
                            onChange={(e: SelectChangeEvent): void => setTargetFolderId((e.target as HTMLInputElement).value)}
                            size='small'
                            variant='outlined'
                            label='Destination'
                        >
                            {selectMenuItems}
                        </Select>
                    </FormControl>
                </form>
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={Math.min(windowSize[0] * 0.8, 500)}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    color='error'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    color='primary'
                    variant='contained'
                    style={{
                        width: '90px',
                        height: '32px',
                        lineHeight: 'normal',
                        marginRight: '10px',
                    }}
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default MoveItemsDialog;